import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  TierChangePreviewDto,
  TierChangePreviewQueryDto,
  ResponseDto,
} from "@mimber/schema"
import { membersKeys } from "../members.utils"

/**
 * Organization-specific hook for previewing the billing outcome of assigning
 * a tier to a member, without making any changes.
 * Can only be used in mimber-organization app
 */
export const useTierChangePreview = (
  memberUuid: string,
  params: Partial<TierChangePreviewQueryDto>,
  options?: UseQueryOptions<ResponseDto<TierChangePreviewDto>, Error>
): UseQueryResult<ResponseDto<TierChangePreviewDto>, Error> => {
  guardAppUsage("mimber-organization", "useTierChangePreview")

  const { server } = useServerContext()

  return useQuery({
    queryKey: membersKeys.previewTierChange(memberUuid, params),
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params.tierId) searchParams.set("tierId", params.tierId)
      if (params.isTierPermanent !== undefined)
        searchParams.set("isTierPermanent", String(params.isTierPermanent))
      if (params.tierChangePolicyOverride)
        searchParams.set(
          "tierChangePolicyOverride",
          params.tierChangePolicyOverride
        )

      const response = await server.get<ResponseDto<TierChangePreviewDto>>(
        `members/${memberUuid}/tier-change-preview?${searchParams.toString()}`
      )
      return response.data
    },
    enabled: !!memberUuid && !!params.tierId,
    ...options,
  })
}
